import { Directive, Input, OnDestroy, OnInit, ElementRef, HostListener } from '@angular/core';
import { NgControl } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, map, catchError, takeUntil } from 'rxjs/operators';

interface NominatimResponse {
  place_id?: number;
  display_name?: string;
  lat?: string;
  lon?: string;
  address?: {
    road?: string;
    city?: string;
    town?: string;
  };
}

export interface StreetSuggestion {
  name: string;
  displayName: string;
  placeId: number;
}

@Directive({
  selector: '[appCheckStreetName]',
  standalone: true,
  exportAs: 'appCheckStreetName',
  host: {
    '(input)': 'onInput($event)',
    '(blur)': 'onBlur()',
    '(focus)': 'onFocus()',
    'autocomplete': 'off'
  }
})
export class CheckStreetNameDirective implements OnInit, OnDestroy {
  @Input() city: string = 'Zielona Góra';
  @Input() country: string = 'Poland';
  @Input() debounceMs: number = 800;
  
  private destroy$ = new Subject<void>();
  public suggestions: StreetSuggestion[] = [];
  private inputSubject$ = new Subject<string>();
  private overlayElement: HTMLElement | null = null;
  private isOpen = false;

  constructor(
    private http: HttpClient,
    private ngControl: NgControl,
    private elementRef: ElementRef<HTMLInputElement>
  ) {
    // Setup observable for street suggestions
    this.inputSubject$.pipe(
      debounceTime(this.debounceMs),
      distinctUntilChanged(),
      switchMap(value => this.getStreetSuggestions(value)),
      takeUntil(this.destroy$)
    ).subscribe(suggestions => {
      this.suggestions = suggestions;
      if (suggestions.length > 0) {
        this.showSuggestions();
      } else {
        this.hideSuggestions();
      }
    });
  }

  ngOnInit(): void {
    // Overlay will be created when first showing suggestions
  }

  onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    if (value && value.length >= 2) {
      this.inputSubject$.next(value);
    } else {
      this.suggestions = [];
      this.hideSuggestions();
    }
  }

  onFocus(): void {
    const value = this.elementRef.nativeElement.value;
    if (value && value.length >= 2 && this.suggestions.length > 0) {
      this.showSuggestions();
    }
  }

  onBlur(): void {
    // Delay to allow click on suggestion
    setTimeout(() => {
      this.hideSuggestions();
    }, 200);
  }

  private showSuggestions(): void {
    if (this.isOpen) return;

    // Clean up existing overlay
    if (this.overlayElement && this.overlayElement.parentNode) {
      this.overlayElement.parentNode.removeChild(this.overlayElement);
      this.overlayElement = null;
    }

    // Get position of the input element
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    
    // Get width from parent mat-form-field or use input width
    const parentElement = this.elementRef.nativeElement.closest('mat-form-field') as HTMLElement;
    const width = parentElement 
      ? parentElement.getBoundingClientRect().width
      : rect.width;

    // Create overlay container with fixed positioning
    this.overlayElement = document.createElement('div');
    this.overlayElement.className = 'street-suggestions-overlay';
    this.overlayElement.style.cssText = `
      position: fixed;
      top: ${rect.bottom}px;
      left: ${rect.left}px;
      width: ${width}px;
      z-index: 1000;
    `;

    const element = this.createSuggestionsElement();
    this.overlayElement.appendChild(element);
    document.body.appendChild(this.overlayElement);
    
    this.isOpen = true;
  }

  private hideSuggestions(): void {
    if (!this.isOpen || !this.overlayElement) return;
    
    if (this.overlayElement.parentNode) {
      this.overlayElement.parentNode.removeChild(this.overlayElement);
    }
    this.overlayElement = null;
    this.isOpen = false;
  }

  private createSuggestionsElement(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'mat-autocomplete-panel';
    container.style.cssText = `
      background: white;
      border-radius: 4px;
      box-shadow: 0 2px 4px -1px rgba(0,0,0,.2), 0 4px 5px 0 rgba(0,0,0,.14), 0 1px 10px 0 rgba(0,0,0,.12);
      max-height: 256px;
      overflow-y: auto;
      width: 100%;
      z-index: 1000;
      border: 1px solid rgba(0,0,0,.12);
    `;

    this.suggestions.forEach(suggestion => {
      const option = document.createElement('div');
      option.className = 'mat-option';
      option.style.cssText = `
        padding: 12px 16px;
        cursor: pointer;
        font-size: 14px;
        line-height: 1.5;
        transition: background-color 0.2s;
        border-bottom: 1px solid rgba(0,0,0,.06);
      `;
      
      // Remove border from last item
      if (this.suggestions.indexOf(suggestion) === this.suggestions.length - 1) {
        option.style.borderBottom = 'none';
      }

      option.innerHTML = `
        <div style="font-weight: 500; color: #015C2BFF;">${suggestion.name}</div>
        <div style="font-size: 12px; color: #666; margin-top: 2px;">${suggestion.displayName}</div>
      `;

      option.addEventListener('mouseenter', () => {
        option.style.backgroundColor = '#f5f5f5';
      });

      option.addEventListener('mouseleave', () => {
        option.style.backgroundColor = 'transparent';
      });

      option.addEventListener('click', () => {
        this.selectSuggestion(suggestion);
      });

      container.appendChild(option);
    });

    return container;
  }

  private selectSuggestion(suggestion: StreetSuggestion): void {
    if (this.ngControl.control) {
      this.ngControl.control.setValue(suggestion.name);
    }
    this.elementRef.nativeElement.value = suggestion.name;
    this.hideSuggestions();
  }

  /**
   * Fetches street suggestions from Nominatim API
   * @param streetName - The street name to search for
   * @returns Observable<StreetSuggestion[]> - Array of matching streets
   */
  private getStreetSuggestions(streetName: string): Observable<StreetSuggestion[]> {
    if (!streetName || streetName.trim().length < 2) {
      return of([]);
    }

    const encodedStreet = encodeURIComponent(streetName.trim());
    const encodedCity = encodeURIComponent(this.city);
    const encodedCountry = encodeURIComponent(this.country);
    
    const url = `https://nominatim.openstreetmap.org/search?street=${encodedStreet}&city=${encodedCity}&country=${encodedCountry}&format=json&limit=10`;

    return this.http.get<NominatimResponse[]>(url).pipe(
      map(response => {
        if (!response || response.length === 0) {
          return [];
        }

        // Extract unique street names
        const uniqueStreets = new Map<string, StreetSuggestion>();
        
        response.forEach(item => {
          const streetName = item.address?.road || item.display_name?.split(',')[0];
          if (streetName && !uniqueStreets.has(streetName)) {
            uniqueStreets.set(streetName, {
              name: streetName,
              displayName: item.display_name || streetName,
              placeId: item.place_id || 0
            });
          }
        });

        return Array.from(uniqueStreets.values());
      }),
      catchError(error => {
        console.warn('Street suggestions API error:', error);
        return of([]);
      })
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.hideSuggestions();
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onWindowEvent(): void {
    if (this.isOpen) {
      this.hideSuggestions();
    }
  }
}
